import React, { useState, useEffect } from 'react';
import './InstallPWA.css';

function InstallPWA() {
    const [showInstall, setShowInstall] = useState(false);
    const [showDialog, setShowDialog] = useState(false);
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [platform, setPlatform] = useState('unknown');
    const [isInstalled, setIsInstalled] = useState(false);

    useEffect(() => {
        // Check if already installed
        const checkInstallation = () => {
            const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
                || window.navigator.standalone 
                || document.referrer.includes('android-app://');
            
            setIsInstalled(isStandalone);
            setShowInstall(!isStandalone);
        };

        checkInstallation();

        // Listen for display mode changes
        const mediaQuery = window.matchMedia('(display-mode: standalone)');
        mediaQuery.addListener(() => checkInstallation());
        
        // Detect platform
        const userAgent = navigator.userAgent.toLowerCase();
        if (/ipad|iphone|ipod/.test(userAgent) && !window.MSStream) {
            setPlatform('ios');
        } else if (/android/.test(userAgent)) {
            setPlatform('android');
        } else if (/windows|macintosh|linux/.test(userAgent)) {
            setPlatform('desktop');
        }

        // Listen for the beforeinstallprompt event
        window.addEventListener('beforeinstallprompt', (e) => {
            if (!isInstalled) {
                e.preventDefault();
                setDeferredPrompt(e);
                setShowInstall(true);
            }
        });

        // Hide install button when app is installed
        window.addEventListener('appinstalled', () => {
            setIsInstalled(true);
            setShowInstall(false);
            setDeferredPrompt(null);
            // Optional: Show a success message
            alert('AroundU has been successfully installed!');
        });

        return () => {
            mediaQuery.removeListener(() => checkInstallation());
        };
    }, [isInstalled]);

    const handleInstallClick = async () => {
        if (deferredPrompt) {
            try {
                deferredPrompt.prompt();
                const { outcome } = await deferredPrompt.userChoice;
                console.log(`User response to the install prompt: ${outcome}`);
                setDeferredPrompt(null);
            } catch (error) {
                console.error('Error installing PWA:', error);
                setShowDialog(true);
            }
        } else {
            setShowDialog(true);
        }
    };

    if (!showInstall) return null;

    return (
        <>
            <button 
                className="install-button" 
                onClick={handleInstallClick}
                aria-label="Install AroundU App"
            >
                <span className="install-icon">⬇️</span>
                Download AroundU
            </button>

            {showDialog && (
                <div className="install-dialog show" onClick={() => setShowDialog(false)}>
                    <div className="dialog-content" onClick={e => e.stopPropagation()}>
                        <div className="dialog-header">
                            <h2>Install AroundU App</h2>
                            <button 
                                className="close-button" 
                                onClick={() => setShowDialog(false)}
                                aria-label="Close dialog"
                            >
                                ✕
                            </button>
                        </div>
                        
                        <div className="dialog-body">
                            {platform === 'ios' && (
                                <div className="install-section">
                                    <h3>📱 Install on iOS</h3>
                                    <p>1. Tap the share icon in Safari <span className="ios-share-icon">⎙</span></p>
                                    <p>2. Scroll down and tap "Add to Home Screen"</p>
                                    <p>3. Tap "Add" to install</p>
                                </div>
                            )}

                            {platform === 'android' && (
                                <div className="install-section">
                                    <h3>📱 Install on Android</h3>
                                    <p>1. Open this website in Chrome</p>
                                    <p>2. Tap the menu icon (⋮)</p>
                                    <p>3. Tap "Add to Home Screen"</p>
                                    <p>4. Tap "Install AroundU"</p>
                                </div>
                            )}

                            {platform === 'desktop' && (
                                <div className="install-section">
                                    <h3>💻 Install on Desktop</h3>
                                    <p>Chrome/Edge:</p>
                                    <p>1. Look for the install icon in the address bar</p>
                                    <p>2. Click "Install AroundU"</p>
                                    <br />
                                    <p>Firefox:</p>
                                    <p>1. Click the menu icon (≡)</p>
                                    <p>2. Click "Install AroundU"</p>
                                </div>
                            )}

                            {platform === 'unknown' && (
                                <div className="install-section">
                                    <h3>⚠️ Installation</h3>
                                    <p>Please use a supported browser like Chrome, Edge, or Safari to install the app.</p>
                                </div>
                            )}
                        </div>
                        
                        <div className="dialog-footer">
                            <button 
                                className="btn btn-secondary" 
                                onClick={() => setShowDialog(false)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default InstallPWA; 